import React from 'react'
import { ArrowDownRight, ArrowUpRight, Minus } from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatCount, formatMoney } from '@/lib/format'
import { formatPoints } from '@/lib/plain-language'

/**
 * Comparison chips for the dashboard.
 *
 * These derive a DIRECTION and a PERCENTAGE / POINT difference for display only.
 * No monetary value is ever computed here: every £ figure on the dashboard is
 * rendered verbatim from the backend through `formatMoney` (CLAUDE.md — "Frontend:
 * display only, zero math"). A chip is a comparison label, not a number the
 * operator can act on in isolation.
 */

export type DeltaDirection = 'up' | 'down' | 'flat'

export interface Delta {
  direction: DeltaDirection
  /** Pre-signed, ready to print: `+12.4%`, `-3.1 pts`, `0%`. */
  label: string
}

/** Round a points delta to one decimal for a readable label (no float noise). */
function round1(n: number): number {
  return Math.round(n * 10) / 10
}

function direction(diff: number): DeltaDirection {
  if (Math.abs(diff) < 0.05) return 'flat'
  return diff > 0 ? 'up' : 'down'
}

function signed(value: number, digits: number, suffix: string): string {
  const rendered = Math.abs(value).toFixed(digits)
  if (value > 0) return `+${rendered}${suffix}`
  if (value < 0) return `-${rendered}${suffix}`
  return `${rendered}${suffix}`
}

/**
 * Percentage change of `current` against `base`. Returns null when `base` is zero
 * or negative — there is no honest percentage against nothing, and the caller
 * should say "no comparison" rather than print a fabricated ∞ or 100%.
 */
export function percentDelta(current: number, base: number): Delta | null {
  if (!(base > 0)) return null
  const diff = current - base
  return { direction: direction(diff), label: signed((diff / base) * 100, 1, '%') }
}

/**
 * Percentage-point difference between two already-percentage figures.
 * The label always reads "+N pts" / "-N pts" — the ONE spelling for a points delta,
 * rendered through the shared `formatPoints` helper (docs/revenue-vocabulary.md §3).
 */
export function pointsDelta(current: number, base: number): Delta {
  const diff = current - base
  return { direction: direction(diff), label: formatPoints(round1(diff)) }
}

/** A backend-supplied point difference (e.g. `monthOnTheBooks.occupancyDeltaPts`). */
export function pointsFrom(pts: number | null | undefined): Delta | null {
  if (pts == null) return null
  return { direction: direction(pts), label: formatPoints(round1(pts)) }
}

/** A backend-supplied percentage delta (e.g. `pace.months[].stlyDeltaPct`). */
export function percentFrom(pct: number | null | undefined): Delta | null {
  if (pct == null) return null
  return { direction: direction(pct), label: signed(pct, 1, '%') }
}

const toneClass: Record<DeltaDirection, string> = {
  up: 'bg-emerald-50 text-emerald-600',
  down: 'bg-rose-50 text-rose-600',
  flat: 'bg-slate-100 text-slate-500',
}

export function DeltaChip({ delta, className }: { delta: Delta; className?: string }) {
  const Icon = delta.direction === 'up' ? ArrowUpRight : delta.direction === 'down' ? ArrowDownRight : Minus
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[11px] font-semibold tabular-nums',
        toneClass[delta.direction],
        className
      )}
    >
      <Icon className="h-3 w-3" />
      {delta.label}
    </span>
  )
}

/** Chip plus its caption, the pattern used under every headline figure. */
export function DeltaRow({ delta, caption }: { delta: Delta; caption: React.ReactNode }) {
  return (
    <div className="flex items-center gap-1.5">
      <DeltaChip delta={delta} />
      <span className="min-w-0 truncate text-[11px] text-muted-foreground">{caption}</span>
    </div>
  )
}

/**
 * Movement figures that are legitimately negative (pickup — real cancellations).
 * `-x` is an exact IEEE sign flip, so the digits the backend sent are preserved
 * to the penny; only the sign is moved outside the currency symbol so `-£412.50`
 * reads as money rather than `£-412.50`.
 */
export function signedMoney(amount: number, currency: string): string {
  if (amount < 0) return `-${formatMoney(-amount, currency)}`
  if (amount > 0) return `+${formatMoney(amount, currency)}`
  return formatMoney(0, currency)
}

/** Signed room/night counts, thousands-separated. `-12`, `+40`, `0`. */
export function signedCount(n: number): string {
  if (n > 0) return `+${formatCount(n)}`
  return formatCount(n)
}

/** Text colour for a movement figure: gains emerald, losses rose, nothing muted. */
export function movementClass(n: number): string {
  if (n > 0) return 'text-emerald-600'
  if (n < 0) return 'text-rose-600'
  return 'text-muted-foreground'
}
